import React from 'react';
import { useTranslate, useDeleteMany, useMany } from "@pankod/refine-core";

import {
  useTable,
  List,
  Table,
  DateField,
  useModal,
  Button,
  Space,
  CreateButton,
  ShowButton,
  EditButton,
  DeleteButton,
  Modal,
  Typography,
  Checkbox,
  FilterDropdown,
  getDefaultFilter,
  Input,
  getDefaultSortOrder,
  useSelect,
  TextField,
  Select,
  NumberField,
  Tag,
} from "@pankod/refine-antd";

import { IQuestion, ICategory } from 'interfaces';

export const QuestionList = () => {
  const translate = useTranslate();
  const { tableProps, sorter, filters, tableQueryResult } = useTable<IQuestion>({
    initialSorter: [
      {
        field: 'id',
        order: 'asc',
      },
    ],
    initialFilter: [
      {
        field: 'content',
        operator: 'contains',
        value: undefined,
      },
    ],
  });

  const defaultColumns = React.useMemo(
    () => [
      {
        label: translate('questions.fields.id'),
        value: 'id',
      },
      {
        label: translate('questions.fields.content'),
        value: 'content',
      },
      {
        label: translate('questions.fields.description'),
        value: 'description',
      },
      {
        label: translate('questions.fields.weight'),
        value: 'weight',
      },
      {
        label: translate('questions.fields.category.title'),
        value: 'category_id',
      },
      {
        label: translate('questions.fields.created_at'),
        value: 'created_at',
      },
      {
        label: translate('questions.fields.updated_at'),
        value: 'updated_at',
      },
    ],
    [],
  );
  const { modalProps: columnsModalProps, show: columnsShow } = useModal();
  const categoriesID = tableProps?.dataSource?.map(item => item.category_id) ?? [];
  const { data: categoryData, isLoading } = useMany<ICategory>({
    resource: 'categories',
    ids: categoriesID,
    queryOptions: {
      enabled: categoriesID.length > 0,
    },
  });
  const { selectProps: categorySelectProps } = useSelect<ICategory>({
    resource: 'categories',
    optionLabel: 'title',
    optionValue: 'id',
  });
  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>([
    'created_at',
    'updated_at',
    'id',
    'weight',
    'description',
  ]);

  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const { mutate, isSuccess, isLoading: deleteManyIsLoading } = useDeleteMany<IQuestion>();
  const deleteSelectedItems = () => {
    mutate({
      resource: 'questions',
      ids: selectedRowKeys.map(String),
    });
  };
  const onSelectChange = (selectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(selectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
  };
  const hasSelected = selectedRowKeys.length > 0;
  const handleHideColumn = (value: string, isChecked: boolean) => {
    if (isChecked) setHiddenColumns(prev => prev.filter(col => col !== value));
    else setHiddenColumns(prev => [...prev, value]);
  };
  const columns = React.useMemo(
    () => [
      <Table.Column<IQuestion>
        key='id'
        dataIndex='id'
        title={translate('questions.fields.id')}
        sorter
        defaultSortOrder={getDefaultSortOrder('id', sorter)}
      />,
      <Table.Column<IQuestion>
        key='content'
        dataIndex='content'
        title={translate('questions.fields.content')}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <Input placeholder={translate('labels.search')} />
          </FilterDropdown>
        )}
        defaultFilteredValue={getDefaultFilter('content', filters)}
      />,
      <Table.Column<IQuestion>
        key='description'
        dataIndex='description'
        title={translate('questions.fields.description')}
      />,
      <Table.Column<IQuestion>
        key='weight'
        dataIndex='weight'
        title={translate('questions.fields.weight')}
        render={value => <NumberField value={value} />}
      />,
      <Table.Column
        key='category_id'
        dataIndex='category_id'
        title={translate('questions.fields.category.title')}
        render={(value: string) => {
          if (isLoading) {
            return <TextField value={translate('loading')} />;
          }
          return <TextField value={categoryData?.data.find((item: ICategory) => item.id === value)?.title} />;
        }}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <Select
              mode='multiple'
              style={{ minWidth: 200 }}
              placeholder={translate('questions.fields.category.filters.placeholder')}
              {...categorySelectProps}
            />
          </FilterDropdown>
        )}
      />,
      <Table.Column<IQuestion>
        key='created_at'
        dataIndex='created_at'
        title={translate('questions.fields.created_at')}
        render={value => <DateField format='LLL' value={value} />}
        sorter
      />,
      <Table.Column<IQuestion>
        key='updated_at'
        dataIndex='updated_at'
        title={translate('questions.fields.updated_at')}
        render={value => <DateField format='LLL' value={value} />}
        sorter
      />,
    ],
    [tableProps, filters, sorter, categoryData, categorySelectProps, isLoading],
  );
  const visibleColumns = React.useMemo(() => {
    return columns.filter(column => !hiddenColumns.includes(column.props.dataIndex));
  }, [hiddenColumns, columns]);

  React.useEffect(() => {
    if (isSuccess) {
      setSelectedRowKeys([]);
    }
  }, [isSuccess]);

  return (
    <>
      <List
        pageHeaderProps={{
          subTitle: hasSelected && (
            <Button danger onClick={deleteSelectedItems} disabled={!hasSelected} loading={deleteManyIsLoading}>
              {`Elimina ${selectedRowKeys.length} oggetti selezionati`}
            </Button>
          ),
          extra: (
            <Space>
              <Tag color='#f1c531'>
                {translate('labels.totalNumber', { resource: translate('questions.questions').toLowerCase() })}
                {tableQueryResult.data?.total || ''}
              </Tag>
              <CreateButton />
              <Button onClick={columnsShow}>{translate('table.hideColumns.buttonTitle')}</Button>
            </Space>
          ),
        }}>
        <Table<IQuestion>
          rowSelection={rowSelection}
          {...tableProps}
          size='small'
          scroll={{ y: '60vh', x: 10 * visibleColumns.length + 'vw' }}
          pagination={{ ...tableProps.pagination, position: ['topLeft'], size: 'small' }}
          rowKey='id'>
          <Table.Column<IQuestion>
            title=''
            dataIndex='actions'
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton title={translate('buttons.show')} size='small' recordItemId={record.id} hideText />
                  <EditButton title={translate('buttons.edit')} size='small' recordItemId={record.id} hideText />
                  <DeleteButton title={translate('buttons.delete')} size='small' recordItemId={record.id} hideText />
                </Space>
              );
            }}
          />
          {visibleColumns}
        </Table>
      </List>
      <Modal footer={null} {...columnsModalProps}>
        <Space direction='vertical'>
          <Typography.Text>{translate('table.hideColumns.title')}</Typography.Text>
          {defaultColumns.map(column => (
            <Checkbox
              key={column.value}
              checked={!hiddenColumns.includes(column.value)}
              onChange={e => handleHideColumn(column.value, e.target.checked)}>
              {column.label}
            </Checkbox>
          ))}
        </Space>
      </Modal>
    </>
  );
};
